import type { ApiResponse } from '../types';

const API_BASE = '/api';

class ApiClient {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on init
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Merge with any existing headers
    const mergedHeaders = {
      ...headers,
      ...(options.headers as Record<string, string> || {}),
    };

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: mergedHeaders,
      });
      
      const data = await response.json();

      if (!response.ok) {
        // Handle 401 - unauthorized
        if (response.status === 401) {
          this.setToken(null);
          window.location.href = '/login';
        }
        return {
          success: false,
          error: data.error || `HTTP error ${response.status}`,
        };
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // Auth endpoints
  async login(username: string, password: string) {
    return this.request<{ user: import('../types').User; token: string; expiresAt: string }>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }
    );
  }

  async register(data: {
    username: string;
    email: string;
    password: string;
    display_name?: string;
    role?: 'Admin' | 'Employee';
  }) {
    return this.request<import('../types').User>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMe() {
    return this.request<{ user: import('../types').User; tenant: import('../types').TenantPublic | null }>('/auth/me');
  }

  async updateProfile(data: { display_name?: string; phone?: string; email?: string }) {
    return this.request<import('../types').User>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async requestPasswordReset(identifier: string) {
    return this.request<{ message: string }>('/auth/password/request-reset', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    });
  }

  async resetPasswordWithToken(token: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // Shift endpoints
  async getShifts() {
    // Add cache busting to ensure fresh data
    return this.request<import('../types').Shift[]>(`/shifts?t=${Date.now()}`);
  }

  async getMyShifts() {
    return this.request<import('../types').Shift[]>('/shifts/my');
  }

  async assignShift(shiftId: string, userId: string) {
    return this.request<import('../types').Shift>(`/shifts/${shiftId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async unassignShift(shiftId: string, userId?: string) {
    const query = userId ? `?userId=${userId}` : '';
    return this.request<import('../types').Shift>(`/shifts/${shiftId}/assign${query}`, {
      method: 'DELETE',
    });
  }

  async createShift(date: string, startTime: string, endTime: string, name?: string | null) {
    return this.request<import('../types').Shift>('/shifts', {
      method: 'POST',
      body: JSON.stringify({ date, start_time: startTime, end_time: endTime, name: name || null }),
    });
  }

  async updateShift(shiftId: string, startTime?: string, endTime?: string, name?: string | null) {
    return this.request<import('../types').Shift>(`/shifts/${shiftId}`, {
      method: 'PUT',
      body: JSON.stringify({ start_time: startTime, end_time: endTime, name: name !== undefined ? (name || null) : undefined }),
    });
  }

  async deleteShift(shiftId: string) {
    return this.request(`/shifts/${shiftId}`, { method: 'DELETE' });
  }

  // Request endpoints
  async getRequests() {
    return this.request<import('../types').ShiftRequest[]>('/requests');
  }

  async getMyRequests() {
    return this.request<import('../types').ShiftRequest[]>('/requests/my');
  }

  async createRequest(shiftId: string) {
    return this.request<import('../types').ShiftRequest>('/requests', {
      method: 'POST',
      body: JSON.stringify({ shiftId }),
    });
  }

  async approveRequest(requestId: string) {
    return this.request<import('../types').ShiftRequest>(`/requests/${requestId}/approve`, {
      method: 'POST',
    });
  }

  async rejectRequest(requestId: string, notes?: string) {
    return this.request<import('../types').ShiftRequest>(`/requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  // Permanent shift endpoints
  // Legacy permanent shifts endpoints - now use shift-templates
  // These are kept for backward compatibility but redirect to shift-templates
  async getPermanentShifts() {
    // Get all shift templates and filter to only those with user_id (permanent shifts)
    const result = await this.getAllShiftTemplates();
    if (result.success && result.data) {
      return {
        ...result,
        data: result.data.filter(t => t.user_id !== null) as any
      };
    }
    return result as any;
  }

  async getAllPermanentShifts() {
    // Get all shift templates and filter to only those with user_id (permanent shifts)
    const result = await this.getAllShiftTemplates();
    if (result.success && result.data) {
      return {
        ...result,
        data: result.data.filter(t => t.user_id !== null) as any
      };
    }
    return result as any;
  }

  async addPermanentShift(dayOfWeek: string, startTime: string, endTime: string) {
    // Deprecated - use addShiftTemplate instead
    return this.request<{ error: string }>('/permanent-shifts', {
      method: 'POST',
      body: JSON.stringify({ dayOfWeek, startTime, endTime }),
    });
  }

  async removePermanentShift(id: string) {
    // Use shift-templates endpoint instead
    return this.request(`/shift-templates/${id}`, { method: 'DELETE' });
  }

  async updatePermanentShift(id: string, startTime?: string, endTime?: string, name?: string | null) {
    // Use shift-templates endpoint instead
    return this.request<any>(`/shift-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ startTime, endTime, name: name !== undefined ? (name || null) : undefined }),
    });
  }

  // Shift template endpoints (unified with permanent shifts)
  async getShiftTemplates() {
    return this.request<Array<{ id: string; day_of_week: string; start_time: string; end_time: string; user_id: string | null; active: number; user?: import('../types').User }>>('/shift-templates');
  }

  async getAllShiftTemplates() {
    return this.request<Array<{ id: string; day_of_week: string; start_time: string; end_time: string; user_id: string | null; active: number; user?: import('../types').User }>>('/shift-templates/all');
  }

  async addShiftTemplate(dayOfWeek: string, startTime: string, endTime: string, userId?: string | null, name?: string | null) {
    return this.request<{ id: string; day_of_week: string; start_time: string; end_time: string; user_id: string | null; active: number }>('/shift-templates', {
      method: 'POST',
      body: JSON.stringify({ dayOfWeek, startTime, endTime, userId: userId || null, name: name || null }),
    });
  }

  async updateShiftTemplate(id: string, startTime?: string, endTime?: string, userId?: string | null, name?: string | null) {
    return this.request<{ id: string; start_time: string; end_time: string; user_id: string | null; active: number }>(`/shift-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ startTime, endTime, userId: userId !== undefined ? userId : undefined, name: name !== undefined ? name : undefined }),
    });
  }

  async removeShiftTemplate(id: string) {
    return this.request(`/shift-templates/${id}`, { method: 'DELETE' });
  }

  // User role management
  async updateUserRole(userId: string, role: 'SuperAdmin' | 'Admin' | 'Employee') {
    return this.request<import('../types').User>(`/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // User endpoints
  async getUsers() {
    return this.request<import('../types').User[]>('/users');
  }

  async getPendingUsers() {
    return this.request<import('../types').User[]>('/users/pending');
  }

  async approveUser(userId: string) {
    return this.request<import('../types').User>(`/users/${userId}/approve`, {
      method: 'POST',
    });
  }

  async rejectUser(userId: string) {
    return this.request<import('../types').User>(`/users/${userId}/reject`, {
      method: 'POST',
    });
  }

  async deactivateUser(userId: string) {
    return this.request<import('../types').User>(`/users/${userId}/deactivate`, {
      method: 'POST',
    });
  }

  async reactivateUser(userId: string) {
    return this.request<import('../types').User>(`/users/${userId}/reactivate`, {
      method: 'POST',
    });
  }

  async adminAddPermanentShift(userId: string, dayOfWeek: string, startTime: string, endTime: string, name?: string | null) {
    // Use shift-templates endpoint - permanent shifts are now shift templates with user_id set
    return this.request<any>('/shift-templates', {
      method: 'POST',
      body: JSON.stringify({ dayOfWeek, startTime, endTime, userId, name: name || null }),
    });
  }

  async adminResetPassword(userId: string, newPassword: string) {
    return this.request(`/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
  }

  async adminCreateUser(data: {
    username: string;
    email: string;
    password: string;
    display_name?: string;
    role?: 'SuperAdmin' | 'Admin' | 'Employee';
  }) {
    return this.request<import('../types').User>('/users/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(userId: string, data: {
    display_name?: string;
    email?: string;
    role?: 'SuperAdmin' | 'Admin' | 'Employee';
    status?: 'Pending' | 'Active' | 'Rejected' | 'Inactive';
    phone?: string | null;
  }) {
    return this.request<import('../types').User>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Admin export/import endpoints
  async exportData(tenantSlug?: string) {
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(`${API_BASE}/admin/export${tenantSlug ? `?tenantSlug=${encodeURIComponent(tenantSlug)}` : ''}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.setToken(null);
          window.location.href = '/login';
        }
        const error = await response.json();
        return {
          success: false,
          error: error.error || `HTTP error ${response.status}`,
        };
      }

      const data = await response.json();
      
      // Download the file
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shift-scheduler-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error('Export failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async importData(importData: any, version?: string) {
    return this.request<{
      message: string;
      summary: {
        users: number;
        usersCreated?: number;
        usersUpdated?: number;
        usersUnchanged?: number;
        shifts: number;
        shiftAssignments: number;
        shiftRequests: number;
        shiftSwaps: number;
        shiftDropRequests: number;
        shiftTemplates: number;
        settings: number;
        clockRecords: number;
        userSettings: number;
        notificationPreferences: number;
        hrIntegrations: number;
        tenants: number;
      };
    }>('/admin/import', {
      method: 'POST',
      body: JSON.stringify({
        data: importData,
        version,
        confirmGlobalImport: true,
        confirmationText: 'REPLACE_ALL_TENANT_DATA',
      }),
    });
  }

  // Tenant export/import (SuperAdmin only)
  async exportTenants() {
    return this.request<{
      data: {
        version: string;
        exportedAt: string;
        exportedBy: string;
        data: {
          tenants: any[];
        };
      };
    }>('/admin/export-tenants', {
      method: 'GET',
    });
  }

  async importTenants(tenants: any[]) {
    return this.request<{
      message: string;
    }>('/admin/import-tenants', {
      method: 'POST',
      body: JSON.stringify({ tenants }),
    });
  }

  // Swap endpoints
  async getMySwaps() {
    return this.request<Array<import('../types').ShiftSwap>>('/swaps/my');
  }

  async createSwapRequest(shiftId: string, targetUserId: string) {
    return this.request<import('../types').ShiftSwap>('/swaps', {
      method: 'POST',
      body: JSON.stringify({ shiftId, targetUserId }),
    });
  }

  async acceptSwap(swapId: string) {
    return this.request(`/swaps/${swapId}/accept`, {
      method: 'POST',
    });
  }

  async rejectSwap(swapId: string) {
    return this.request(`/swaps/${swapId}/reject`, {
      method: 'POST',
    });
  }

  async cancelSwap(swapId: string) {
    return this.request(`/swaps/${swapId}/cancel`, {
      method: 'POST',
    });
  }

  // Drop endpoints
  async getDropRequests() {
    return this.request<Array<import('../types').ShiftDropRequest>>('/drops');
  }

  async getMyDropRequests() {
    return this.request<Array<import('../types').ShiftDropRequest>>('/drops/my');
  }

  async createDropRequest(shiftId: string) {
    return this.request<import('../types').ShiftDropRequest>('/drops', {
      method: 'POST',
      body: JSON.stringify({ shiftId }),
    });
  }

  async approveDropRequest(requestId: string) {
    return this.request<import('../types').ShiftDropRequest>(`/drops/${requestId}/approve`, {
      method: 'POST',
    });
  }

  async rejectDropRequest(requestId: string, notes?: string) {
    return this.request<import('../types').ShiftDropRequest>(`/drops/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  // Settings endpoints
  async getSettings() {
    return this.request<Record<string, any>>('/settings');
  }

  async updateSettings(settings: Record<string, any>) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
  }

  async getUserSettings() {
    return this.request<any>('/settings/user');
  }

  async updateUserSettings(settings: {
    email_notifications_enabled?: boolean;
    email_shift_assigned?: boolean;
    email_shift_reminder?: boolean;
    email_request_status?: boolean;
    email_shift_reminder_night_before?: boolean;
    email_shift_reminder_2hours_before?: boolean;
  }) {
    return this.request('/settings/user', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Clock in/out endpoints
  async clockIn() {
    return this.request<{ clock_in_time: string; ip_address: string }>('/clock/in', {
      method: 'POST',
    });
  }

  async clockOut() {
    return this.request<{ clock_in_time: string; clock_out_time: string; ip_address: string }>('/clock/out', {
      method: 'POST',
    });
  }

  async getClockStatus() {
    return this.request<{ is_clocked_in: boolean; clock_in_time: string | null; clock_in_ip: string | null }>('/clock/status');
  }

  async getClockRecords(startDate?: string, endDate?: string, targetUserId?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (targetUserId) params.append('targetUserId', targetUserId);
    const query = params.toString();
    return this.request<any[]>(`/clock/records${query ? `?${query}` : ''}`);
  }

  // HR integrations endpoints
  async getHRIntegrations() {
    return this.request<any[]>('/hr-integrations');
  }

  async saveHRIntegration(data: {
    id?: string;
    platform_name: string;
    api_key?: string;
    api_secret?: string;
    base_url?: string;
    enabled?: boolean;
    config_json?: any;
  }) {
    return this.request('/hr-integrations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteHRIntegration(id: string) {
    return this.request(`/hr-integrations/${id}`, {
      method: 'DELETE',
    });
  }

  // User management endpoints (updateUser is defined above)

  async deleteUser(userId: string) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Tenant management endpoints (super admin only)
  async getTenants() {
    return this.request<any[]>('/tenants');
  }

  async getTenant(id: string) {
    return this.request<any>(`/tenants/${id}`);
  }

  async createTenant(data: {
    name: string;
    slug: string;
    subdomain?: string | null;
    plan?: 'free' | 'basic' | 'pro' | 'enterprise';
    status?: 'Active' | 'Suspended' | 'Cancelled';
    admin?: {
      username: string;
      email: string;
      password: string;
      display_name?: string;
    };
  }) {
    return this.request<any>('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTenant(id: string, data: {
    name?: string;
    slug?: string;
    subdomain?: string | null;
    plan?: 'free' | 'basic' | 'pro' | 'enterprise';
    status?: 'Active' | 'Suspended' | 'Cancelled';
  }) {
    return this.request<any>(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTenant(id: string) {
    return this.request(`/tenants/${id}`, {
      method: 'DELETE',
    });
  }

  /** Super Admin: list all users with their current tenant (for reassigning users to correct org) */
  async getUsersWithTenants() {
    return this.request<Array<import('../types').User & { tenant_id: string; tenant_name: string }>>('/users/with-tenants');
  }

  /** Super Admin: move a user to a different tenant so they see that org's data */
  async reassignUserToTenant(userId: string, tenantId: string) {
    return this.request<import('../types').User>(`/users/${userId}/tenant`, {
      method: 'PUT',
      body: JSON.stringify({ tenantId }),
    });
  }

  // Database migration (super admin only)
  async migrateToPostgres() {
    return this.request<{ message: string }>('/admin/migrate-to-postgres', {
      method: 'POST',
    });
  }

  async checkShiftSource(dayOfWeek: string, startTime: string, endTime?: string) {
    const params = new URLSearchParams({ dayOfWeek, startTime });
    if (endTime) params.append('endTime', endTime);
    return this.request<{
      dayOfWeek: string;
      startTime: string;
      endTime: string | null;
      permanentShifts: Array<{
        id: string;
        userId: string;
        userName: string;
        userEmail: string | null;
        userStatus: string | null;
        dayOfWeek: string;
        startTime: string;
        endTime: string;
        name: string | null;
        active: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
      shiftTemplates: Array<{
        id: string;
        userId: string | null;
        userName: string;
        userEmail: string | null;
        userStatus: string | null;
        dayOfWeek: string;
        startTime: string;
        endTime: string;
        name: string | null;
        active: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
    }>(`/admin/check-shift-source?${params.toString()}`);
  }

  async regenerateShifts() {
    return this.request<{ message: string }>('/admin/regenerate-shifts', {
      method: 'POST',
    });
  }

  async checkShiftExists(date: string, startTime: string, endTime?: string) {
    const params = new URLSearchParams({ date, startTime });
    if (endTime) params.append('endTime', endTime);
    return this.request<{
      exists: boolean;
      message?: string;
      shift?: {
        id: string;
        date: string;
        startTime: string;
        endTime: string;
        status: string;
        assignmentType: string;
        name: string | null;
        assignedUsers: Array<{
          id: string;
          username: string;
          displayName: string | null;
          email: string;
        }>;
        createdAt: string;
        updatedAt: string;
      };
    }>(`/admin/check-shift-exists?${params.toString()}`);
  }
}

export const api = new ApiClient();
